import { themes } from '@weenat/theme';
import { weenatIntl } from '@weenat/wintl';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import ceil from 'lodash-es/ceil';
import floor from 'lodash-es/floor';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import getMax from 'lodash-es/max';
import getAvg from 'lodash-es/mean';
import getMin from 'lodash-es/min';
import getSum from 'lodash-es/sum';
import moment from 'moment-timezone';
import { scaleLinear } from 'victory-vendor/d3-scale';
import { DataSources } from '../../enums';
import { isForecastDataSource, isMeasurementDataSource, isMeteoVisionDataSource, isSpatializedWeatherDataSource, isUserReportDataSource, isWeatherDataSource } from '../../enums/DataSources';
import TimeSteps from '../../enums/TimeSteps';
import { declineColorForHorizon } from '../colors';
import { getYDomainPerMetric } from '../measurements/measurements';
const theme = themes.light;
/**
 * Checks all the entries inside the dataset to check if they're null
 */
export const getDataSourceLabel = (ds, capitalize) => {
    let msgKey = 'unknown';
    if (!isNil(ds)) {
        if (isMeasurementDataSource(ds)) {
            msgKey = 'measure';
        }
        if (isWeatherDataSource(ds)) {
            msgKey = 'weather';
        }
        if (isSpatializedWeatherDataSource(ds)) {
            msgKey = 'spatialized_weather';
        }
        if (isForecastDataSource(ds)) {
            msgKey = 'forecast';
        }
        if (isUserReportDataSource(ds)) {
            msgKey = 'corrected_measure';
        }
        if (isMeteoVisionDataSource(ds)) {
            msgKey = 'meteo_vision';
        }
        if (ds === DataSources.climate_normal) {
            msgKey = 'climate_normal';
        }
    }
    const key = `dataSources.${msgKey}`;
    return weenatIntl.t(key, { capitalize });
};
/**
 * Checks all the entries inside the dataset to check if they're null
 */
export const isDataSetNull = (dataSet) => {
    return !dataSet.some((d) => d.y !== null);
};
/**
 * Returns an object with the minimum, maximum, sum & average of the given dataset
 * Might be replace by meta data from back end.
 * @deprecated remove with chartWithD3
 */
export const getAggregatesFromChartData = (dataSet) => {
    const values = !isNil(dataSet)
        ? dataSet.reduce((acc, datum) => {
            if (!isNil(datum.value))
                acc.push(datum.value);
            return acc;
        }, [])
        : undefined;
    let min = !isEmpty(values) ? getMin(values) : null;
    let max = !isEmpty(values) ? getMax(values) : null;
    let sum = !isEmpty(values) ? getSum(values) : null;
    let avg = !isEmpty(values) ? getAvg(values) : null;
    //force null
    if (min === undefined) {
        min = null;
    }
    if (max === undefined) {
        max = null;
    }
    if (sum === undefined) {
        sum = null;
    }
    if (avg === undefined) {
        avg = null;
    }
    return {
        min,
        max,
        sum,
        avg
    };
};
export function extractHistoryValues(data, metric, aggregateToExtract) {
    const rawData = data.values[metric] || {};
    let dataSet = [];
    if (!isEmpty(rawData)) {
        if (!isNil(aggregateToExtract) && !isNil(rawData[aggregateToExtract])) {
            dataSet = rawData[aggregateToExtract];
        }
        else {
            dataSet = rawData?.avg ?? rawData?.sum ?? [];
        }
    }
    return [...dataSet];
}
/**
 * Formats an HistoryRecord for sums (sum) or average (avg) aggregates
 * into an array of formatted data points to be used in charts
 */
export const formatHistoryDataForCharts = ({ data, metric, aggregateToExtract, conversionFct }) => {
    const dataSet = extractHistoryValues(data, metric, aggregateToExtract);
    return dataSet.map((rawPoint) => {
        const value = conversionFct(rawPoint.value);
        return {
            // date object is same for all timezone so don't use moment here for perf matter
            x: new Date(rawPoint.timestamp * 1000),
            y: value,
            value,
            timestamp: rawPoint.timestamp,
            // @ts-expect-error not all metrics are translated (mainly forecasts ones)
            label: weenatIntl.t(`metrics.${metric}`),
            // @ts-expect-error not all metrics are part of the theme (mainly forecasts ones)
            color: theme.colors.metrics[metric]['500'],
            metric,
            source: rawPoint.source
        };
    });
};
/**
 * Normalize the given point
 * value will be convert from domain range to [0,1]
 */
export const normalizeDataPoint = (domain) => {
    const scale = scaleLinear().range([0, 1]).domain(domain);
    return (point) => {
        let result;
        if (isNil(point)) {
            result = point;
        }
        else if (domain[1] - domain[0] === 0) {
            result = 0;
        }
        else {
            result = scale(point);
            // result = (point - domain[0]) / (domain[1] - domain[0])
        }
        return result;
    };
};
/**
 * Formats an HistoryRecord for sums (sum) or average (avg) aggregates
 * into an array of formatted data points to be used in charts
 */
export const formatMeasurementReportForCharts = ({ data, metric, conversionFct, yDomain }) => {
    const normalizeFct = normalizeDataPoint(yDomain);
    return data.map((rawPoint) => {
        const { value } = rawPoint;
        const numberValue = typeof value === 'number'
            ? value
            : typeof value === 'string'
                ? parseFloat(value)
                : value?.dose ?? null;
        return {
            x: new Date(parseInt(rawPoint.timestamp, 10) * 1000),
            y: normalizeFct(numberValue),
            value: conversionFct(numberValue),
            timestamp: parseInt(rawPoint.timestamp, 10),
            label: weenatIntl.t(`metrics.IRR`),
            color: theme.colors.metrics.IRR['500'],
            metric,
            source: DataSources.user_measurement_report
        };
    });
};
/**
 * Formats an HistoryRecord for sums (sum) or average (avg) aggregates
 * into an array of formatted data points to be used in charts
 */
export const formatCumulativeSumHistoryForCharts = ({ data, metric, conversionFct }) => {
    const now = new Date();
    return Object.entries(data).map(([timestamp, cumulativeValue]) => {
        const value = conversionFct(cumulativeValue);
        const x = new Date(parseInt(timestamp, 10) * 1000);
        return {
            x,
            y: value,
            value,
            timestamp: parseInt(timestamp, 10),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            label: weenatIntl.t(`metrics.${metric}`),
            color: theme.colors.metrics[metric]['800'],
            metric,
            source: x.getTime() > now.getTime() ? DataSources.forecast : DataSources.measurement
        };
    });
};
/**
 * Formats an HistoryRecord for sums (sum) or average (avg) aggregates
 * into an array of formatted data points to be used in charts
 */
export const formatHorizonHistoryDataForCharts = ({ metric, data, conversionFct, isAggregateBySerial, colorBySerial, dataToDisplay }) => {
    const rawData = data.values[metric] || {};
    const result = {};
    const getHorizonDepth = (horizonIdNb) => data.horizons?.[metric]?.[horizonIdNb]?.value;
    const horizonHistoryValueToFormattedDataPoint = (horizonIdNb) => (rawPoint) => {
        const value = conversionFct(rawPoint.value);
        const color = !isNil(rawPoint.serial)
            ? colorBySerial?.[metric]?.[rawPoint.serial].color
            : undefined;
        return {
            x: new Date(rawPoint.timestamp * 1000),
            y: value,
            value,
            timestamp: rawPoint.timestamp,
            label: isAggregateBySerial
                ? `${getHorizonDepth(horizonIdNb)} cm (${rawPoint.serial})`
                : `${getHorizonDepth(horizonIdNb)} cm`,
            color: isAggregateBySerial && !isNil(color)
                ? color
                : declineColorForHorizon(metric, data.horizons?.[metric]?.[horizonIdNb]),
            metric,
            source: rawPoint.source
        };
    };
    return Object.entries(rawData).reduce((acc, [horizonId, values]) => {
        const horizonIdNb = parseInt(horizonId, 10);
        if (isAggregateBySerial) {
            values.forEach((v) => {
                if (!isNil(v.serial)) {
                    const key = `${getHorizonDepth(horizonIdNb)};${v.serial}`;
                    if (dataToDisplay === undefined || dataToDisplay[key] === true) {
                        let valuesBySerial = acc[key];
                        if (isNil(valuesBySerial)) {
                            valuesBySerial = [];
                            acc[key] = valuesBySerial;
                        }
                        valuesBySerial.push(horizonHistoryValueToFormattedDataPoint(horizonIdNb)(v));
                    }
                }
            });
            return acc;
        }
        else {
            if (dataToDisplay === undefined || dataToDisplay[horizonIdNb] === true) {
                acc[horizonIdNb] = values.map(horizonHistoryValueToFormattedDataPoint(horizonIdNb));
            }
            return acc;
        }
    }, result);
};
export const getXDomain = ({ data, defaultXDomain }) => {
    let result = defaultXDomain;
    if (!isEmpty(data)) {
        const timestamps = data.map((point) => point.timestamp);
        const min = Math.min(...timestamps);
        const max = Math.max(...timestamps);
        result = [new Date(min * 1000), new Date(max * 1000)];
    }
    return result;
};
export const getYDomain = ({ metric, timeStep, unit, data, shouldDefault = true, customMax, customMin }) => {
    const { min, max } = getAggregatesFromChartData(data);
    const [defaultMin, defaultMax] = shouldDefault
        ? getYDomainPerMetric({ metric, timeStep, unit, min: min, max: max })
        : [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
    /**
     * if a value is greater than predefined domains max
     * then we applied a ratio to add some space at the top of the chart
     */
    const ratio = max !== null && max >= defaultMax && defaultMax > (customMax ?? Number.MIN_SAFE_INTEGER)
        ? 1.2
        : 1;
    return [
        floor(Math.min(min, customMin ?? defaultMin), -0.5),
        ceil(Math.max(max, customMax ?? defaultMax) * ratio, -0.5)
    ];
};
export const getAbsoluteYDomain = ({ metric, timeStep, unit, data }) => {
    const { min, max } = getAggregatesFromChartData(data);
    const defaultMetricDomain = getYDomainPerMetric({
        metric,
        timeStep,
        unit
    });
    return [
        Math.min(floor(min, -0.5), defaultMetricDomain[0]),
        Math.max(ceil(max, -0.5), defaultMetricDomain[1])
    ];
};
export const getChartDomains = ({ metric, timeStep, unit, data, defaultXDomain = [new Date(), new Date()], customMax, customMin }) => {
    const xDomain = getXDomain({
        data,
        defaultXDomain
    });
    const yDomain = getYDomain({
        metric: metric,
        unit,
        timeStep,
        data,
        customMax,
        customMin,
        //we don't want ratio (ie value multiply by 1.5 to increase graph) here since variation is very small on this unit
        shouldDefault: metric === 'SWC' || metric === 'ECP' ? false : true
    });
    const absoluteYDomain = getAbsoluteYDomain({
        metric,
        unit,
        timeStep,
        data
    });
    return {
        absoluteYDomain,
        xDomain,
        yDomain
    };
};
/**
 * Denormalize the given point
 * value will be convert from [0,1] to domain range
 */
export const denormalizeDataPoint = (domain) => {
    const scale = scaleLinear().range(domain);
    return (point) => {
        let result;
        if (isNil(point)) {
            result = point;
        }
        else {
            result = scale.domain([0, 1])(point);
            // result = Math.ceil(point * (domain[1] - domain[0]) - domain[0])
        }
        return result;
    };
};
export const createFakeHistoryValues = ({ period, timeStep, allowNegatives = false, max = 100 }) => {
    const data = [];
    const since = moment.unix(typeof period.since === 'string' ? parseInt(period.since, 10) : period.since);
    const before = moment.unix(typeof period.before === 'string' ? parseInt(period.before, 10) : period.before);
    const current = since;
    const step = 0.35;
    const source = DataSources.measurement;
    let x = 1;
    const verticalTranslation = allowNegatives ? 0 : 1;
    const amplitude = allowNegatives ? max : max / 2;
    while (current.isBefore(before)) {
        let timestamp;
        switch (timeStep) {
            case TimeSteps.perHours:
                timestamp = current.add(1, 'hour').unix();
                break;
            case TimeSteps.perDays:
                timestamp = current.add(1, 'day').unix();
                break;
            case TimeSteps.perWeek:
                timestamp = current.add(1, 'week').unix();
                break;
            case TimeSteps.perMonth:
                timestamp = current.add(1, 'month').unix();
                break;
            case TimeSteps.perYears:
                timestamp = current.add(1, 'year').unix();
                break;
            default:
                timestamp = null;
                break;
        }
        if (!isNil(timestamp)) {
            const value = (Math.sin(x) + verticalTranslation) * amplitude;
            x += step;
            data.push({ timestamp, value, source });
        }
    }
    return data;
};
export const metricsWithGradient = ['T', 'U', 'SSI'];
export const getLabels = ({ datum }) => `${datum.color}:${datum.label}:${isNil(datum.value) ? '' : datum.value}:${datum.metric}:${getDataSourceLabel(datum.source)}`;
export const splitLabels = (serializedLabel) => {
    const [color, label, value, metric, source] = serializedLabel.split(':');
    return { color, label, value, metric, source };
};
